<script>
  import { navigate } from 'svelte-routing';
  import { api } from '../lib/api.js';
  import CaptureRow from '../components/CaptureRow.svelte';
  import CaptureHelp from '../components/CaptureHelp.svelte';
  import { ui } from '../lib/ui.svelte.js';

  let { id } = $props();
  let bucket = $state(null);
  let captures = $state([]);
  let loading = $state(true);
  let err = $state('');

  // Inline slug editor.
  let editingSlug = $state(false);
  let slugDraft = $state('');
  let savingSlug = $state(false);

  async function load() {
    loading = true; err = '';
    try {
      bucket = await api.getBucket(id);
      const r = await api.listCaptures({ bucket_id: id, limit: 200 });
      captures = r.captures || [];
    } catch (e) { err = e.message; }
    finally { loading = false; }
  }

  // Re-load whenever the route's :id changes (rename swaps the URL via
  // navigate(..., {replace:true}) but the component instance stays mounted).
  $effect(() => { id; load(); });

  function startEditSlug() {
    slugDraft = bucket.id;
    editingSlug = true;
  }

  async function saveSlug() {
    const next = slugDraft.trim();
    if (!next || next === bucket.id) { editingSlug = false; return; }
    savingSlug = true;
    try {
      const updated = await api.updateBucket(bucket.id, { slug: next });
      ui.snack('URL updated', 'success');
      editingSlug = false;
      // The bucket's id changed -- replace the URL and re-load against
      // the new id so any future edits target the right resource.
      navigate(`/buckets/${updated.id}`, { replace: true });
    } catch (e) { ui.snack(e.message, 'error'); }
    finally { savingSlug = false; }
  }

  async function clearAll() {
    const ok = await ui.ask({
      title: 'Clear all captures?',
      message: 'Every capture in this bucket will be permanently removed. The bucket itself is kept.',
      confirmText: 'Clear all',
      destructive: true,
    });
    if (!ok) return;
    try { await api.bulkDeleteCaptures(id); ui.snack('Captures cleared', 'success'); load(); }
    catch (e) { ui.snack(e.message, 'error'); }
  }

  async function deleteBucket() {
    const ok = await ui.ask({
      title: 'Delete bucket?',
      message: 'The bucket and all of its captures will be permanently removed.',
      confirmText: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try { await api.deleteBucket(id); ui.snack('Bucket deleted', 'success'); navigate('/buckets'); }
    catch (e) { ui.snack(e.message, 'error'); }
  }

  function urlFor() { return window.location.origin + '/h/' + id; }

  function onDeleted(capId) {
    captures = captures.filter((c) => c.id !== capId);
  }
</script>

{#if err}
  <p class="text-error">{err}</p>
{:else if !bucket}
  <p class="text-on-surface-variant">Loading...</p>
{:else}
  <div class="flex items-center gap-3 mb-4">
    <button onclick={() => history.back()} class="p-2 rounded-full hover:bg-surface-container-high">
      <span class="material-symbols-outlined">arrow_back</span>
    </button>
    <h2 class="text-2xl font-medium">{bucket.name}</h2>
    <div class="ml-auto flex gap-2">
      <button onclick={clearAll} class="px-4 py-2 rounded-full text-sm hover:bg-surface-container-high">
        Clear captures
      </button>
      <button onclick={deleteBucket} class="px-4 py-2 rounded-full text-sm text-error hover:bg-error/10">
        Delete bucket
      </button>
    </div>
  </div>

  <section class="bg-surface-container rounded-2xl p-4 mb-4">
    {#if editingSlug}
      <div class="bucket-slug-edit">
        <span class="bucket-slug-prefix">{window.location.origin}/h/</span>
        <input
          bind:value={slugDraft}
          class="bucket-slug-input"
          placeholder="my-bucket-slug"
          autocomplete="off"
          spellcheck="false"
          onkeydown={(e) => {
            if (e.key === 'Enter') saveSlug();
            if (e.key === 'Escape') editingSlug = false;
          }}
        />
        <button onclick={saveSlug} disabled={savingSlug} class="bucket-slug-btn-primary">
          {savingSlug ? 'Saving…' : 'Save'}
        </button>
        <button onclick={() => (editingSlug = false)} disabled={savingSlug} class="bucket-slug-btn-text">
          Cancel
        </button>
      </div>
      <p class="bucket-slug-hint">
        Lowercase letters, digits and hyphens (3–64 chars). Existing captures are kept and re-pointed.
      </p>
    {:else}
      <div class="flex items-center gap-3">
        <span class="material-symbols-outlined text-on-surface-variant">link</span>
        <code class="flex-1 font-mono text-sm break-all">{urlFor()}</code>
        <button onclick={startEditSlug} class="p-2 rounded-full hover:bg-surface-container-high" title="Edit URL slug">
          <span class="material-symbols-outlined text-base">edit</span>
        </button>
        <button onclick={() => navigator.clipboard.writeText(urlFor())} class="p-2 rounded-full hover:bg-surface-container-high" title="Copy URL">
          <span class="material-symbols-outlined text-base">content_copy</span>
        </button>
      </div>
    {/if}
  </section>

  <CaptureHelp bucketId={bucket.id} />

  {#if bucket.description}
    <p class="text-on-surface-variant mb-4">{bucket.description}</p>
  {/if}

  {#if captures.length === 0}
    <div class="bg-surface-container rounded-2xl p-12 text-center text-on-surface-variant">
      Send a request to the URL above to see captures appear here.
    </div>
  {:else}
    <div class="bg-surface-container rounded-2xl divide-y divide-outline-variant overflow-hidden">
      {#each captures as c (c.id)}
        <CaptureRow capture={c} showBucket={false} {onDeleted} />
      {/each}
    </div>
  {/if}
{/if}

<style>
  .bucket-slug-edit {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
  .bucket-slug-prefix {
    font-family: 'Menlo', 'Consolas', monospace;
    font-size: 13px;
    color: rgb(var(--md-sys-color-on-surface-variant));
  }
  .bucket-slug-input {
    flex: 1;
    min-inline-size: 200px;
    padding: 0.5rem 0.75rem;
    border: 1px solid rgb(var(--md-sys-color-outline));
    border-radius: 8px;
    background: rgb(var(--md-sys-color-surface));
    color: rgb(var(--md-sys-color-on-surface));
    font-family: 'Menlo', 'Consolas', monospace;
    font-size: 13px;
  }
  .bucket-slug-input:focus {
    outline: none;
    border-color: rgb(var(--md-sys-color-primary));
    border-width: 2px;
    padding: calc(0.5rem - 1px) calc(0.75rem - 1px);
  }
  .bucket-slug-btn-primary {
    padding: 0.5rem 1.125rem;
    border-radius: 999px;
    background: rgb(var(--md-sys-color-primary));
    color: rgb(var(--md-sys-color-on-primary));
    font-size: 13px;
    font-weight: 500;
    border: 0;
    cursor: pointer;
  }
  .bucket-slug-btn-primary:hover { opacity: 0.9; }
  .bucket-slug-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .bucket-slug-btn-text {
    padding: 0.5rem 1rem;
    border-radius: 999px;
    background: transparent;
    color: rgb(var(--md-sys-color-on-surface-variant));
    font-size: 13px;
    border: 0;
    cursor: pointer;
  }
  .bucket-slug-btn-text:hover { background: rgb(var(--md-sys-color-on-surface) / 0.04); }
  .bucket-slug-hint {
    margin-block-start: 0.5rem;
    font-size: 12px;
    color: rgb(var(--md-sys-color-on-surface-variant));
  }
</style>
